// src/containers/pages/caseManagement/components/DeleteCaseConfirmDialog.tsx

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  MuiList as List,
  ListItemText,
  Typography,
} from 'components/mui';
import { ListItem, useTheme } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';
import { CaseDeletabilityData } from 'api/cases/types';

interface DeleteCaseConfirmDialogProps {
  open: boolean;
  deletability: CaseDeletabilityData | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ALSO_REMOVED_LABELS: Record<
  keyof CaseDeletabilityData['alsoRemoved'],
  string
> = {
  emails: 'Email history entries',
  formSubmissions: 'Submitted forms',
  scheduleRows: 'Schedule rows',
  caseReferences: 'Case cross-references',
  parentChildLinks: 'Parent/child case links',
  statusHistoryRows: 'Status-history entries',
  pinnedBy: 'User pins',
  keywords: 'Keyword links',
  contactLinks: 'Contact links',
};

const MAX_LISTED_FILES = 20; // no component-level scroll region (FE-D9 / UI_UX_STANDARDS)

/** Red/orange confirmation for the guarded case delete (design §6 dialogs). */
function DeleteCaseConfirmDialog({
  open,
  deletability,
  deleting,
  onClose,
  onConfirm,
}: DeleteCaseConfirmDialogProps) {
  const theme = useTheme();
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  if (!deletability) return null;

  const isWarn = deletability.state === 'WARN_ATTACHMENTS';
  // A failed delete re-fetches; the fresh state can be BLOCKED_* while this dialog
  // is still open - the confirm must lock and the reason must be visible (FE-D11)
  const isBlocked = !(isWarn || deletability.state === 'DELETABLE');
  const disclosures = (
    Object.keys(ALSO_REMOVED_LABELS) as (keyof typeof ALSO_REMOVED_LABELS)[]
  ).filter((k) => deletability.alsoRemoved[k] > 0);
  const confirmDisabled = deleting || isBlocked || (isWarn && !acknowledged);
  const listedFiles = deletability.attachments.slice(0, MAX_LISTED_FILES);

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderTop: `4px solid ${theme.palette.error.main}` },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon sx={{ color: 'warning.main' }} />
        Are you sure you want to delete this case?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {deletability.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Case ID: {deletability.caseId}
        </Typography>

        {disclosures.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Also removed with this case:
            </Typography>
            <List dense disablePadding>
              {disclosures.map((k) => (
                <ListItem key={k} disableGutters sx={{ py: 0 }}>
                  <ListItemText
                    primary={`${ALSO_REMOVED_LABELS[k]}: ${deletability.alsoRemoved[k]}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {deletability.flags.gpdaDefault && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This is a GPDA default case; the admin tree tool may re-create it
            empty.
          </Alert>
        )}

        {isBlocked && (
          <Alert severity="error" sx={{ mt: 1 }}>
            This case can no longer be deleted - its state changed. Close this
            dialog; the button now shows why.
          </Alert>
        )}

        {isWarn && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">
              If you delete this case, the following{' '}
              {deletability.attachments.length} file(s) will become permanently
              unavailable:
            </Alert>
            <List dense>
              {listedFiles.map((f) => (
                <ListItem
                  key={`${f.population}-${f.sourceId}`}
                  disableGutters
                  sx={{ py: 0 }}
                >
                  <ListItemText
                    primary={f.originalFilename ?? `(unnamed #${f.sourceId})`}
                    secondary={f.population}
                  />
                </ListItem>
              ))}
            </List>
            {deletability.attachments.length > MAX_LISTED_FILES && (
              <Typography variant="body2" color="text.secondary">
                ...and {deletability.attachments.length - MAX_LISTED_FILES}{' '}
                more file(s) - the full list is recorded in the delete log.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Files remain on the server and can be reclaimed from the delete
              log.
            </Typography>
            {deletability.keptCommentFiles.length > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {deletability.keptCommentFiles.length} file(s) belonging to
                moved comments will be kept.
              </Typography>
            )}
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAcknowledged(e.target.checked)
                  }
                />
              }
              label="I understand these files will become unavailable"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={confirmDisabled}
          startIcon={
            deleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteIcon />
            )
          }
        >
          {deleting
            ? 'Deleting...'
            : isWarn
              ? 'Yes, delete this case'
              : 'Delete case'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteCaseConfirmDialog;
